import { ReactNode } from 'react';

import { ChakraProvider, extendTheme, Theme } from '@chakra-ui/react';

import { semanticColors } from 'components/theme/colors';
import { theme as baseTheme } from 'components/theme/theme';

interface PaymentThemeProviderProps {
  children: ReactNode;
}

const paymentSemanticTokens = {
  colors: {
    background: {
      default: semanticColors['grey.offWhite'].default,
      _dark: '#0f0f0f',
    },
    'grey.offWhite': {
      default: semanticColors['grey.offWhite'].default,
      _dark: '#0f0f0f',
    },
    'grey.white': {
      default: semanticColors['grey.white'].default,
      _dark: '#1a1a1a',
    },
    'payment.primary': {
      default: '#4477CE',
      _dark: '#4477CE',
    },
    'payment.primary.hover': {
      default: '#3A6BB8',
      _dark: '#3A6BB8',
    },
    'payment.primary.disabled': {
      default: '#4477CE66',
      _dark: '#4477CE66',
    },
    'payment.primaryText': {
      default: '#4477CE',
      _dark: '#4477CE',
    },
    'payment.background': {
      default: '#EDF1FA',
      _dark: '#EDF1FA',
    },
    'payment.cardBackground': {
      default: '#FFFFFF',
      _dark: '#FFFFFF',
    },
    'payment.timeBar': {
      default: '#EDF1FA',
      _dark: 'rgba(255, 255, 255, 0.05)',
    },
    'payment.timeBarBorder': {
      default: 'transparent',
      _dark: 'transparent',
    },
    'payment.timeBarText': {
      default: 'grey.primaryText',
      _dark: 'grey.primaryText',
    },
    'payment.notice': {
      default: '#EDF1FA',
      _dark: '#EDF1FA',
    },
    'payment.noticeBorder': {
      default: 'transparent',
      _dark: 'transparent',
    },
    'payment.noticeText': {
      default: 'gray.700',
      _dark: 'gray.700',
    },
    'payment.qrBackground': {
      default: '#EDF1FA',
      _dark: 'rgba(255, 255, 255, 0.05)',
    },
    'payment.qrBorder': {
      default: 'transparent',
      _dark: 'transparent',
    },
    'payment.titleText': {
      default: 'gray.600',
      _dark: 'gray.600',
    },
    'payment.cloudColor': {
      default: '#2E3ECD',
      _dark: '#2E3ECD',
    },
    'payment.arrowColor': {
      default: '#6BAFFF',
      _dark: '#6BAFFF',
    },
    'payment.qrBorderWidth': {
      default: '0',
      _dark: '0',
    },
    'payment.timeBarBorderWidth': {
      default: '0',
      _dark: '0',
    },
  },
};

const paymentComponents: Theme['components'] = {
  ...baseTheme.components,
  Button: {
    ...(baseTheme.components?.Button ?? {}),
    variants: {
      ...baseTheme.components?.Button?.variants,
      payment: {
        backgroundColor: 'payment.primary',
        color: 'white',
        cursor: 'pointer',
        _hover: {
          backgroundColor: 'payment.primary.hover',
        },
        _disabled: {
          backgroundColor: 'payment.primary.disabled',
        },
        _focus: {
          ring: 0,
          outline: 'none',
        },
      },
      paymentOutline: {
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderColor: 'payment.primary',
        color: 'payment.primary',
        cursor: 'pointer',
        _dark: {
          color: 'white',
          borderColor: 'payment.primary',
        },
        _hover: {
          backgroundColor: 'rgba(68, 119, 206, 0.1)',
          color: 'payment.primary',
          borderColor: 'payment.primary',
          _dark: {
            backgroundColor: 'rgba(68, 119, 206, 0.1)',
            color: 'white',
            borderColor: 'payment.primary',
          },
        },
        _disabled: {
          backgroundColor: 'transparent',
          borderColor: 'payment.primary.disabled',
          color: 'payment.primary.disabled',
        },
        _focus: {
          ring: 0,
          outline: 'none',
        },
      },
      grey: {
        ...baseTheme.components?.Button?.variants?.grey,
        cursor: 'pointer',
        _hover: {
          backgroundColor: '#d0d0d1',
          outline: 'none',
          boxShadow: 'none',
          _dark: {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          },
        },
        _dark: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          color: 'grey.primaryText',
        },
      },
      greyOutline: {
        backgroundColor: 'transparent',
        borderWidth: '2px',
        borderColor: 'grey.border',
        color: 'grey.secondaryText',
        cursor: 'pointer',
        _dark: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'grey.secondaryText',
        },
        _hover: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderColor: 'grey.border',
          _dark: {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
        },
        _focus: {
          ring: 0,
          outline: 'none',
        },
      },
    },
  },
};

const paymentTheme = extendTheme(baseTheme, {
  config: {
    initialColorMode: 'light',
    useSystemColorMode: true,
  },
  components: paymentComponents,
  semanticTokens: {
    colors: {
      ...semanticColors,
      ...paymentSemanticTokens.colors,
    },
  },
});

export function PaymentThemeProvider({ children }: PaymentThemeProviderProps) {
  return <ChakraProvider theme={paymentTheme}>{children}</ChakraProvider>;
}
